import React from 'react'
import { Typography, makeStyles } from '@material-ui/core'
import {
  getTimezone,
  formatCurrency,
  formatName,
  getPipelines,
  getQuoteTypeOption,
  getEventTypes,
  getCrews,
  getTaskTemplateOptions
} from '@paintscout/util/builder'
import { joinWithOr } from '@paintscout/util'
import type { PipelineStage } from 'paintscout'
import type {
  Activity,
  EventActivityDetails,
  FileAddedActivityDetails,
  MessageActivityDetails,
  PaymentActivityDetails,
  PaymentReceivedSentActivityDetails,
  PaymentRequestedActivityDetails,
  StageChangedActivityDetails,
  TaskActivityDetails,
  ViewedActivityDetails
} from '@paintscout/api'
import startCase from 'lodash/startCase'
import { parseNumber } from '@paintscout/util/calculator'
import DesktopIcon from '@material-ui/icons/DesktopMac'
import PhoneIcon from '@material-ui/icons/PhoneIphone'
import moment from 'moment'
import { useLocation } from 'react-router-dom'
import Tooltip from '@paintscout/ui/components/Tooltip'
import { useIsMobile } from '@paintscout/ui/hooks'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import { useUsers } from '../../providers/UsersProvider'
import { withLiveOwnerName } from '../../util/withLiveOwnerName'
import ReadCheck from './ReadCheck'

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    marginTop: 2,
    overflowWrap: 'anywhere'
  },

  readNoWrap: {
    whiteSpace: 'nowrap'
  },

  deviceIcon: {
    width: '13px',
    height: '13px',
    marginLeft: theme.spacing(1)
  }
}))

interface QuoteActivitySubtitleProps {
  activity: Activity
  showDocTypeDetails: boolean
}

const ACTIVITY_TYPE_FILE_ADDED = 'file-added'
const ACTIVITY_TYPE_TAGS_CHANGED = 'tags-changed'
const ACTIVITY_TYPE_EVENT_PROGRESS_UPDATED = 'event-progress-updated'

/**
 * The common subtitle for Quote activities
 */
export default function QuoteActivitySubtitle(props: QuoteActivitySubtitleProps) {
  const classes = useStyles({})
  const mobile = useIsMobile({ xs: true })
  const { options } = useClientOptions()
  const { usersById } = useUsers()
  const timezone = getTimezone({ options })
  const { activity, showDocTypeDetails } = props

  const quote = activity.details?.quote || activity.details?.invoice
  const contact = activity.details?.contact
  // The owner snapshot is frozen at write time, so a renamed seat keeps rendering the previous
  // person's name. Resolve the live name the same way ActivityAvatar does (#21800).
  const owner = withLiveOwnerName(activity.details?.owner, usersById)
  const location = useLocation()
  const eventTypes = getEventTypes({ options, includeDeleted: true })
  const { amountPaid, method, provider, surcharge } = activity.details as PaymentActivityDetails
  const isRefund = !!(activity.details as any)?.isRefund
  const originalAmount: number | undefined = (activity.details as any)?.originalAmount
  const { device } = activity.details as ViewedActivityDetails
  const { message } = activity.details as MessageActivityDetails
  const readAt = message?.readAt
  const isCustomerMessage = message?.from?.isCustomer
  const parsedSurcharge = parseNumber(surcharge)
  const activityType = activity.details?.activityType
  const isQuotePage = location.pathname.includes('/quotes/') || location.pathname.includes('/invoices/')
  const isDealPage = location.pathname.includes('/deals/')

  let total: string | number
  if (activityType === 'payment') {
    if (parsedSurcharge) {
      total = parseNumber(amountPaid) - parsedSurcharge
    } else {
      total = amountPaid
    }
  } else if (activityType === 'payment-requested') {
    total = (activity.details as PaymentRequestedActivityDetails)?.amountRequested
  } else if (activityType === 'payment-received-sent') {
    total = (activity.details as PaymentReceivedSentActivityDetails)?.amountPaid
  } else if (activity.details?.docType === 'deal' && activityType !== 'deal-stage-changed') {
    total = activity.details?.deal?.total
  } else {
    total = activity.details?.quote?.total ?? activity.details?.invoice?.total ?? undefined
  }

  const quoteType = quote?.int_ext?.value
    ? quote?.int_ext?.label
      ? quote?.int_ext.label
      : getQuoteTypeOption({ options, quoteType: quote?.int_ext?.value })?.label
    : ''

  const showUser = showDocTypeDetails && owner && isCustomerMessage
  const showContact =
    showDocTypeDetails &&
    !isDealPage &&
    contact &&
    !['accepted'].includes(activityType) &&
    !isCustomerMessage &&
    !location.pathname.includes('contact') &&
    !(location.pathname.includes('deals') && [ACTIVITY_TYPE_FILE_ADDED].includes(activityType))

  const showQuoteType = showDocTypeDetails && !!quoteType && location.pathname.includes('people')

  let totalLine: string
  if (isRefund && originalAmount) {
    totalLine = `${formatCurrency({ options, value: Math.abs(parseFloat(amountPaid as any)) })} of ${formatCurrency({ options, value: originalAmount })}`
  } else if (parsedSurcharge) {
    totalLine = `${formatCurrency({ options, value: total as number })} + ${formatCurrency({ options, value: parsedSurcharge })} (surcharge)`
  } else {
    totalLine = formatCurrency({ options, value: total as number })
  }

  const showTotalLine =
    total !== undefined &&
    ![ACTIVITY_TYPE_TAGS_CHANGED, ACTIVITY_TYPE_FILE_ADDED, 'lead-form-submission'].includes(activityType)

  const formattedProvider = provider === 'payrix' ? 'PaintScout Payments' : startCase(provider)

  const fileName = (activity.details as FileAddedActivityDetails)?.fileName
  let taskName = (activity.details as TaskActivityDetails)?.taskName
  let taskTemplateName
  if ((activity.details as TaskActivityDetails).templateId) {
    const templates = getTaskTemplateOptions({ options })
    const template = templates.find((t) => t.key === (activity.details as TaskActivityDetails).templateId)
    if (template) {
      taskTemplateName = template.label
      if (activityType === 'task-created') {
        taskName = null
      }
    }
  }

  const oldStageId = (activity.details as StageChangedActivityDetails)?.oldStage
  const newStageId = (activity.details as StageChangedActivityDetails)?.newStage
  const pipelines = getPipelines({ options })
  const stages = pipelines.reduce((acc, pipeline) => {
    return [...acc, ...(pipeline.stages || [])]
  }, [] as PipelineStage[])
  const oldStageName = stages.find((stage) => stage.key === oldStageId)?.label
  const newStageName = stages.find((stage) => stage.key === newStageId)?.label

  const oldStagePipeline = pipelines.find((pipeline) => pipeline.stages?.some((stage) => stage.key === oldStageId))
  const newStagePipeline = pipelines.find((pipeline) => pipeline.stages?.some((stage) => stage.key === newStageId))
  const isSamePipeline = oldStagePipeline?.key === newStagePipeline?.key

  const eventType = activityType === 'event-email-sent' ? (activity.details as EventActivityDetails)?.eventType : ''
  const translatedEventType = eventTypes.find((et) => et.key === eventType || et.label === eventType)?.label

  const getEventDate = () => {
    const startDate = (activity.details as EventActivityDetails)?.start
    const endDate = (activity.details as EventActivityDetails)?.end
    const skipDateOnEventActivity =
      (activityType === ACTIVITY_TYPE_EVENT_PROGRESS_UPDATED &&
        (activity.details as EventActivityDetails)?.progress !== 100) ||
      activity.details?.events?.[0] === 'event-cancelled'

    if (!startDate || skipDateOnEventActivity) return ''

    if (endDate && !moment(startDate).tz(timezone).isSame(endDate, 'day')) {
      // Multi-day event
      if (!moment(startDate).tz(timezone).isSame(endDate, 'month')) {
        // Different months
        return `${moment(startDate).tz(timezone).format('MMMM DD')} - ${moment(endDate).tz(timezone).format('MMMM DD')}`
      }
      // Same month
      return `${moment(startDate).tz(timezone).format('MMMM DD')}-${moment(endDate).tz(timezone).format('DD')}`
    } else {
      // Single-day event
      return moment(startDate).tz(timezone).format('MMMM Do, hh:mma')
    }
  }

  const formattedEventDate = getEventDate()
  const stageChangeLine =
    oldStageName && newStageName
      ? `${oldStageName}${!isSamePipeline ? ` (${oldStagePipeline?.label})` : ''} ➞ ${newStageName}${!isSamePipeline ? ` (${newStagePipeline?.label})` : ''}`
      : ''

  const dealName = activity.details?.deal?.name
  const showDealName = showDocTypeDetails && dealName && !location.pathname.includes('deals')

  const tags = activityType === ACTIVITY_TYPE_TAGS_CHANGED ? joinWithOr(activity.details.events, 'and') : ''

  const showQuoteNumber =
    (showDocTypeDetails || (activityType === ACTIVITY_TYPE_TAGS_CHANGED && !isQuotePage)) && quote?.number

  let crew = null
  if (
    (activity.details as any)?.crew &&
    !(
      activityType === ACTIVITY_TYPE_EVENT_PROGRESS_UPDATED &&
      (activity.details as EventActivityDetails)?.progress !== 100
    )
  ) {
    const crewOptions = getCrews({ options })
    crew = crewOptions.find((crew) => crew._id === (activity.details as any).crew)
  }

  let progressUpdateLine = null
  if (activityType === ACTIVITY_TYPE_EVENT_PROGRESS_UPDATED) {
    const oldProgress = (activity.details as EventActivityDetails)?.previousProgress || 0
    const newProgress = (activity.details as EventActivityDetails)?.progress || 0
    if (oldProgress !== newProgress && newProgress !== 100) {
      progressUpdateLine = `${oldProgress}% ➞ ${newProgress}%`
    }
  }

  const subtitle = [
    showQuoteNumber && `#${quote?.number}`,
    taskName && taskName,
    taskTemplateName && taskTemplateName,
    stageChangeLine,
    fileName && fileName,
    tags,
    formattedEventDate,
    crew ? crew.name : null,
    progressUpdateLine,
    showDealName && dealName,
    showContact && formatName(contact),
    showQuoteType && quoteType,
    showUser && formatName(owner),
    translatedEventType,
    showTotalLine && totalLine,
    !!provider && formattedProvider,
    !provider && !!method && startCase(method)
  ]
    .filter(Boolean)
    .join(' · ')

  // The read line is rendered as JSX (not folded into the joined string) so the checkmark can
  // sit right after "Read x ago". Split off the last word ("ago") so it + the checkmark wrap to
  // a new line together rather than the checkmark ending up stranded on its own line.
  const readFromNow = readAt ? moment(readAt).fromNow() : ''
  const lastSpace = readFromNow.lastIndexOf(' ')
  const readPrefix = lastSpace >= 0 ? readFromNow.slice(0, lastSpace + 1) : ''
  const readLastWord = lastSpace >= 0 ? readFromNow.slice(lastSpace + 1) : readFromNow

  return (
    <Typography variant={mobile ? 'body1' : 'subtitle2'} component="span" className={classes.root}>
      {subtitle}
      {/* The read checkmark sits right after "Read x ago" on every breakpoint; "ago" + the
          checkmark are wrapped together so they break to a new line as one unit */}
      {readAt && (
        <>
          {subtitle && ' · '}
          {`Read ${readPrefix}`}
          <span className={classes.readNoWrap}>
            {readLastWord}
            <ReadCheck readAt={readAt} />
          </span>
        </>
      )}
      {device === 'desktop' && (
        <Tooltip hideIcon content="Viewed on computer">
          <DesktopIcon className={classes.deviceIcon} />
        </Tooltip>
      )}
      {device === 'mobile' && (
        <Tooltip hideIcon content="Viewed on mobile device">
          <PhoneIcon className={classes.deviceIcon} />
        </Tooltip>
      )}
    </Typography>
  )
}
